import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import Footer from './components/Footer';
import TopBar from './components/TopBar';
import legalPages from './data/legalPages';
import DashboardPage from './pages/DashboardPage';
import HomePage from './pages/HomePage';
import LegalPage from './pages/LegalPage';
import LoginPage from './pages/LoginPage';
import PayPalCheckoutPage from './pages/PayPalCheckoutPage';
import RequestDeletePage from './pages/RequestDeletePage';
import SignupPage from './pages/SignupPage';
import { apiRequest } from './services/api';
import { PAYPAL_POPUP_PATH } from './services/constants';
import { getCurrentPath, navigate } from './services/navigation';
import {
  clearStoredSession,
  getStoredSession,
  REFRESH_TOKEN_KEY,
  saveStoredSession,
  TOKEN_KEY,
} from './services/session';

function App() {
  const initialSession = getStoredSession();
  const [path, setPath] = useState(getCurrentPath());
  const [user, setUser] = useState(initialSession.user);
  const [checkingSession, setCheckingSession] = useState(Boolean(initialSession.token));
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const isAuthenticated = Boolean(user);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    setDashboardError('');

    try {
      const [me, activity] = await Promise.all([
        apiRequest('/users/me'),
        apiRequest('/users/me/transactions'),
      ]);

      setUser(me);
      setTransactions(Array.isArray(activity) ? activity : []);
      saveStoredSession({
        token: localStorage.getItem(TOKEN_KEY) || '',
        refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || '',
        user: me,
      });
    } catch (err) {
      if ((err.message || '').toLowerCase() === 'unauthorized') {
        clearStoredSession();
        setUser(null);
        setTransactions([]);
        navigate('/login');
      } else {
        setDashboardError(err.message || 'Unable to load dashboard');
      }
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setPath(getCurrentPath());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type === 'talkali-paypal-purchase-complete') {
        loadDashboard();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [loadDashboard]);

  useEffect(() => {
    let active = true;

    const loadSession = async () => {
      const session = getStoredSession();

      if (!session.token) {
        if (active) {
          setCheckingSession(false);
        }
        return;
      }

      try {
        const me = await apiRequest('/users/me');

        if (!active) {
          return;
        }

        setUser(me);
        saveStoredSession({
          token: session.token,
          refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || session.refreshToken,
          user: me,
        });
      } catch (_) {
        clearStoredSession();

        if (!active) {
          return;
        }

        setUser(null);
      } finally {
        if (active) {
          setCheckingSession(false);
        }
      }
    };

    loadSession();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (checkingSession) {
      return;
    }

    if (path === '/dashboard' && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (path === PAYPAL_POPUP_PATH && !isAuthenticated) {
      navigate('/login');
      return;
    }

    if (isAuthenticated && (path === '/login' || path === '/signup')) {
      navigate('/dashboard');
    }
  }, [checkingSession, path, isAuthenticated]);

  useEffect(() => {
    if (checkingSession || path !== '/dashboard' || !isAuthenticated) {
      return;
    }

    loadDashboard();
  }, [checkingSession, path, isAuthenticated, loadDashboard]);

  const handlePasswordLogin = async (email, password) => {
    const data = await apiRequest('/login', {
      method: 'POST',
      auth: false,
      body: {
        email: email.trim(),
        password,
      },
    });

    const token = data?.token || '';
    const refreshToken = data?.refresh_token || '';
    const nextUser = data?.user || null;

    if (!token || !refreshToken || !nextUser) {
      throw new Error('Invalid login response');
    }

    saveStoredSession({
      token,
      refreshToken,
      user: nextUser,
    });

    setUser(nextUser);
    setDashboardError('');
    navigate('/dashboard');
  };

  const handleRequestCode = async (email) => {
    await apiRequest('/login/request-code', {
      method: 'POST',
      auth: false,
      body: {
        email: email.trim(),
      },
    });
  };

  const handleCodeLogin = async (email, code) => {
    const data = await apiRequest('/login/verify-code', {
      method: 'POST',
      auth: false,
      body: {
        email: email.trim(),
        code: code.trim(),
      },
    });

    const token = data?.token || '';
    const refreshToken = data?.refresh_token || '';
    const nextUser = data?.user || null;

    if (!token || !refreshToken || !nextUser) {
      throw new Error('Invalid login response');
    }

    saveStoredSession({
      token,
      refreshToken,
      user: nextUser,
    });

    setUser(nextUser);
    setDashboardError('');
    navigate('/dashboard');
  };

  const handleRequestDelete = async (email, password) => {
    await apiRequest('/users/request-account-deletion', {
      method: 'POST',
      auth: false,
      body: {
        email: email.trim(),
        password,
      },
    });
  };

  const handleLogout = async () => {
    try {
      await apiRequest('/logout', {
        method: 'POST',
        body: {},
      });
    } catch (_) {}

    clearStoredSession();
    setUser(null);
    setTransactions([]);
    setDashboardError('');
    navigate('/');
  };

  const handleAddCoins = () => {
    const width = 540;
    const height = 760;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      `${window.location.origin}${PAYPAL_POPUP_PATH}`,
      'talkali-paypal-checkout',
      `popup=yes,width=${width},height=${height},left=${left},top=${top}`
    );

    if (!popup) {
      window.alert('Please allow popups to continue to PayPal checkout.');
      return;
    }

    popup.focus();
  };

  let currentPage;

  if (checkingSession) {
    currentPage = (
      <main className="page-shell loading-shell">
        <div className="loading-state large-loading">Checking session...</div>
      </main>
    );
  } else if (path === '/login') {
    currentPage = (
      <LoginPage
        onPasswordLogin={handlePasswordLogin}
        onCodeLogin={handleCodeLogin}
        onRequestCode={handleRequestCode}
        busyExternal={false}
      />
    );
  } else if (path === '/signup') {
    currentPage = <SignupPage />;
  } else if (path === '/request-delete') {
    currentPage = <RequestDeletePage onRequestDelete={handleRequestDelete} />;
  } else if (path === '/dashboard') {
    currentPage = (
      <DashboardPage
        user={user}
        transactions={transactions}
        loading={dashboardLoading}
        error={dashboardError}
        onAddCoins={handleAddCoins}
        onLogout={handleLogout}
      />
    );
  } else if (path === PAYPAL_POPUP_PATH) {
    currentPage = <PayPalCheckoutPage />;
  } else if (legalPages[path]) {
    currentPage = <LegalPage page={legalPages[path]} />;
  } else {
    currentPage = <HomePage />;
  }

  if (path === PAYPAL_POPUP_PATH) {
    return <div className="app-root">{currentPage}</div>;
  }

  return (
    <div className="app-root">
      <TopBar user={user} onLogout={handleLogout} />
      {currentPage}
      <Footer />
    </div>
  );
}

export default App;